import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {MomentDatePipe, PreviewDocumentService, TransformationModule, TranslationModule} from '@utils-lib';
import {DE_LANG} from '../assets/i18n/de';
import {
  DatevCoreModule,
  DatevLoaderModule,
  DatevMessageModule,
  DatevNotificationModule
} from '@datev/ngx-ui-components';
import {HttpClientModule} from '@angular/common/http';
import {environment} from '../environments/environment';
import {AuthenticationModule, AuthenticationService, ReLoginModule} from '@datev/ngx-dcal';
import {GlobalErrorHandler} from './global-error-handler';
import {ErrorComponent} from './error/error.component';
import {UebersichtComponent} from './uebersicht/uebersicht.component';
import {LicenseComponent} from './license/license.component';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {AppInjector} from './app-injector';
import {FragebogenTypPipe} from './fragebogen/fragebogen-typ.pipe';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FormsModule} from '@angular/forms';
import {TabbarComponent} from './uebersicht/tabbar/tabbar.component';
import {TabComponent} from './uebersicht/tabbar/tab/tab.component';
import {DataTableComponent} from './uebersicht/tabbar/tab/data-table/data-table.component';
import {UserMenuModule} from '@datev-mydatev/usermenu';
import {SharedModule} from './shared/shared.module';
import {NachdigalService} from '@nachdigal';
import {FragebogenComponent} from './fragebogen/fragebogen.component';
import {ReloginComponent} from '@common-ui-lib';
import {AgreementsModule} from '@datev-mydatev/agreements';
import {ContextModule, ContextService, MYDATEV_APPLICATION_CONTEXT, MyDatevContext} from '@datev-mydatev/context';
import {PersonalcounterModule} from '@datev-mydatev/personalcounter';

import {catchError, filter} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {AppswitchModule} from '@datev-mydatev/app-switch';
import {ConvertOldTaskDataService} from './shared/service/convert-old-task-data.service';
import {PdfViewerModule} from './pdf-viewer/pdf-viewer.module';
import {AttachmentService} from './shared/service/attachment.service';
import {DatevLanguageModule} from '@datev-mydatev/language';

registerLocaleData(localeDe);


export function initializeDatevModules(authService: AuthenticationService, contextService: ContextService) {
  return () => {
    authService.isLoggedIn
      .pipe(
        filter((isLoggedIn) => isLoggedIn),
        catchError((error) => throwError(error))
      ).subscribe(() => {
      contextService.evaluateContext();
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,
    FragebogenComponent,
    ErrorComponent,
    UebersichtComponent,
    LicenseComponent,
    FragebogenTypPipe,
    TabbarComponent,
    TabComponent,
    DataTableComponent,
    ReloginComponent
  ],
  imports: [
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    AgreementsModule.forRoot({
      auto: true,
      categories: ['global', 'steuern'],
    }),
    PdfViewerModule,
    ContextModule.forRoot(),
    SharedModule,
    DatevLanguageModule.forRoot({ apiOrigin: 'api/cnr' }),
    PersonalcounterModule.forRoot({
      forceXmlHttpRequest: false,
    }),
    DatevCoreModule.forRoot(),
    DatevNotificationModule.forRoot(),
    DatevLoaderModule.forRoot(),
    DatevMessageModule.forRoot(),
    UserMenuModule.forRoot(),
    TransformationModule,
    TranslationModule.forChild({
      deLang: DE_LANG
    }),
    AuthenticationModule.forRoot({
      contextRoot: environment.contextRoot,
      options: {
        inactivityTimeout: environment.production,
        federation: true,
        disableSessionManagement: environment.disableSessionManagement
      }
    }),
    ReLoginModule.forRoot({
      reloginHandler: ReloginComponent
    }),
    AppswitchModule
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: 'Window',
      useValue: window
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    NachdigalService,
    FragebogenTypPipe,
    MomentDatePipe,
    PreviewDocumentService,
    TranslationModule,
    ConvertOldTaskDataService,
    AttachmentService,
    {
      provide: MYDATEV_APPLICATION_CONTEXT,
      useValue: MyDatevContext.Kanzlei
    },
    {
      provide: APP_INITIALIZER,
      deps: [AuthenticationService, ContextService],
      useFactory: initializeDatevModules,
      multi: true
    }
  ],
  exports: []
})
export class AppModule {
  constructor(injector: Injector) {
    AppInjector.setInjector(injector);
  }
}
